import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthRefresher } from '@/features/auth/AuthRefresher';
import { useAuthStore } from '@/features/auth/store';
import { AdminLayout } from '@/layouts/AdminLayout';
import { LoginPage } from '@/pages/LoginPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { UsersPage } from '@/pages/users/UsersPage';
import { UserCreatePage } from '@/pages/users/UserCreatePage';
import { UserEditPage } from '@/pages/users/UserEditPage';
import { UserDetailPage } from '@/pages/users/UserDetailPage';
import { HistoryPage } from '@/pages/history/HistoryPage';
import { ReviewsPage } from '@/pages/reviews/ReviewsPage';
import { BidsPage } from '@/pages/bids/BidsPage';
import { BidDetailPage } from '@/pages/bids/BidDetailPage';
import { RepairReferralPage } from '@/pages/bids/RepairReferralPage';
import { FinesPage } from '@/pages/fines/FinesPage';
import { CommissionsPage } from '@/pages/commissions/CommissionsPage';
import { SeoPagesPage } from '@/pages/seo/SeoPagesPage';
import { SeoPageEditPage } from '@/pages/seo/SeoPageEditPage';
import { SeoCitiesPage } from '@/pages/seo/SeoCitiesPage';
import { SeoBrandsPage } from '@/pages/seo/SeoBrandsPage';
import { SeoModelsPage } from '@/pages/seo/SeoModelsPage';
import { SeoWorksPage } from '@/pages/seo/SeoWorksPage';
import { CitiesPage } from '@/pages/place/CitiesPage';
import { RegionsPage } from '@/pages/place/RegionsPage';
import { CountriesPage } from '@/pages/place/CountriesPage';
import { WorksPage } from '@/pages/works/WorksPage';
import { VerificationPage } from '@/pages/verification/VerificationPage';
import { VerificationContactsPage } from '@/pages/verification/VerificationContactsPage';
import { VerificationLegalPage } from '@/pages/verification/VerificationLegalPage';
import { VerificationSpecPage } from '@/pages/verification/VerificationSpecPage';
import { VerificationPhotoPage } from '@/pages/verification/VerificationPhotoPage';
import { StatOnlinePage } from '@/pages/stat/StatOnlinePage';
import { StatCountPage } from '@/pages/stat/StatCountPage';
import { StatListPage } from '@/pages/stat/StatListPage';
import { StatAnalyticsPage } from '@/pages/stat/StatAnalyticsPage';
import { StatStoListPage } from '@/pages/stat/StatStoListPage';
import {
  ExportFranchiseServicesPage,
  ExportBidsPage,
  ExportRejectedBidsPage,
  ExportBillingPage,
  ExportServicePage,
  ExportCartsPage,
  ExportMarketingPaymentPage,
  ExportLoyaltyPage,
  ExportLoyaltyBidsPage,
  ExportPartOrdersPage,
  ExportUsersPage,
} from '@/pages/export/ExportPages';
import { AccrualPointsPage } from '@/pages/export/AccrualPointsPage';
import { BillingLogPage } from '@/pages/billing/BillingLogPage';
import { BillingPaymentPage } from '@/pages/billing/BillingPaymentPage';
import { BillingWithdrawalPage } from '@/pages/billing/BillingWithdrawalPage';
import { CallCenterPage } from '@/pages/callcenter/CallCenterPage';
import { CancelledBidsPage } from '@/pages/callcenter/CancelledBidsPage';
import { CallsMapPage } from '@/pages/callcenter/CallsMapPage';
import { BATCH_ROUTES } from './batchRoutes';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

const RequireAuth = ({ children }: { children: React.ReactNode }) => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isBootstrapping = useAuthStore((s) => s.isBootstrapping);
  if (isBootstrapping) return <div style={{ padding: 40 }}>Загрузка…</div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
};

export const App = () => {
  const bootstrap = useAuthStore((s) => s.bootstrap);
  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthRefresher />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            element={
              <RequireAuth>
                <AdminLayout />
              </RequireAuth>
            }
          >
            <Route path="/users" element={<UsersPage />} />
            <Route path="/users/create" element={<UserCreatePage />} />
            <Route path="/users/edit/:id" element={<UserEditPage />} />
            <Route path="/users/user/:id" element={<UserDetailPage />} />
            <Route path="/history" element={<HistoryPage />} />
            <Route path="/reviews" element={<ReviewsPage />} />
            <Route path="/bids/bids" element={<BidsPage />} />
            <Route path="/bids/fines" element={<FinesPage />} />
            <Route path="/bids/commissions" element={<CommissionsPage />} />
            <Route path="/bids/bid/:id" element={<BidDetailPage />} />
            <Route path="/bids/repair-referral/:id" element={<RepairReferralPage />} />
            <Route path="/seo/pages" element={<SeoPagesPage />} />
            <Route path="/seo/pages/edit/:id" element={<SeoPageEditPage />} />
            <Route path="/seo/cities" element={<SeoCitiesPage />} />
            <Route path="/seo/brands" element={<SeoBrandsPage />} />
            <Route path="/seo/models" element={<SeoModelsPage />} />
            <Route path="/seo/works" element={<SeoWorksPage />} />
            <Route path="/place/cities" element={<CitiesPage />} />
            <Route path="/place/regions" element={<RegionsPage />} />
            <Route path="/place/countries" element={<CountriesPage />} />
            <Route path="/works" element={<WorksPage />} />
            <Route path="/verification" element={<VerificationPage />} />
            <Route path="/verification/contacts/:id" element={<VerificationContactsPage />} />
            <Route path="/verification/legal/:id" element={<VerificationLegalPage />} />
            <Route path="/verification/specialization/:id" element={<VerificationSpecPage />} />
            <Route path="/verification/photo/:id" element={<VerificationPhotoPage />} />
            <Route path="/stat/online" element={<StatOnlinePage />} />
            <Route path="/stat/count" element={<StatCountPage />} />
            <Route path="/stat/list" element={<StatListPage />} />
            <Route path="/stat/analytics" element={<StatAnalyticsPage />} />
            <Route path="/stat/stat-sto-list" element={<StatStoListPage />} />
            <Route path="/home" element={<Navigate to="/stat/online" replace />} />

            <Route path="/export/export-franchise-services" element={<ExportFranchiseServicesPage />} />
            <Route path="/export/export-bids" element={<ExportBidsPage />} />
            <Route path="/export/export-rejected-bids" element={<ExportRejectedBidsPage />} />
            <Route path="/export/export-billing-payment" element={<ExportBillingPage />} />
            <Route path="/export/export-service-payment" element={<ExportServicePage />} />
            <Route path="/export/export-carts" element={<ExportCartsPage />} />
            <Route path="/export/export-marketing-payment" element={<ExportMarketingPaymentPage />} />
            <Route path="/export/export-billing-loyalty" element={<ExportLoyaltyPage />} />
            <Route path="/export/export-loyalty" element={<ExportLoyaltyBidsPage />} />
            <Route path="/export/export-part-orders" element={<ExportPartOrdersPage />} />
            <Route path="/export/export-users" element={<ExportUsersPage />} />
            <Route path="/export/accrual-points" element={<AccrualPointsPage />} />

            <Route path="/billing/billing-log" element={<BillingLogPage />} />
            <Route path="/billing/billing-payment" element={<BillingPaymentPage />} />
            <Route path="/billing/billing-withdrawal" element={<BillingWithdrawalPage />} />

            <Route path="/callcenter/center" element={<CallCenterPage />} />
            <Route path="/callcenter/cancelled-bids" element={<CancelledBidsPage />} />
            <Route path="/callcenter/calls-map" element={<CallsMapPage />} />

            {BATCH_ROUTES.map((r) => (
              <Route key={r.path} path={r.path} element={r.element} />
            ))}

            <Route path="*" element={<NotFoundPage />} />
          </Route>
          <Route path="/" element={<Navigate to="/stat/online" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
};
